import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import FileUpload from '../Registration/common/FileUpload';
import CreateOrderForm from './CreateOrderForm';
import RegistrSelector from '../../components/lists/RegistrSelector';
import DatePicker from '../Registration/common/Calendar/DatePicker';
import apiClient from '../../api/client';
import {
    getCachedCities,
    getCachedCustomerOrder,
    getCachedServiceCatalog,
    invalidateFrontendCache,
    updateFrontendCache,
} from '../../api/frontendCache';
import { invalidatePersonalAccountCache } from './personalAccountCache';
import Chat from '../Registration/common/Chat/Chat.jsx';

import '../Main/Main.css';
import '../Registration/Registration.css';
import '../Executor/Executor.css';
import '../Customer/Customer.css';
import '../Customer/Customer.mobile.css';
import '../Customer/Customer.tablet.css';
import '../../styles/Modal.css';
import '../Customer/PersonalAccount.css';   
import '../Customer/PersonalAccount.tablet.css'
import '../Customer/OrderEdit.css';

import icon_user from '../../assets/Main/icon_user.svg';
import edit from '../../assets/Main/icon_edit_order.svg';
import back from '../../assets/Main/arrow_left_bold.svg';
import tab_detail from '../../assets/Main/tab_detail.svg';
import tab_chat from '../../assets/Main/tab_chat.svg';
import tab_specialists from '../../assets/Main/tab_profile.svg';
import close from '../../assets/Main/icon_close.svg';
import btn_delete from '../../assets/Main/btn_delete.svg';



export default function OrderEdit() {
    const routerLocation = useLocation()
    const navigate = useNavigate();
    const { orderId } = useParams();
    const [cities, setCities] = useState([]);
    const [loadingCities, setLoadingCities] = useState(false);
    const [activeTab, setActiveTab] = useState('details');
    const [showCreateForm, setShowCreateForm] = useState(false)
    const [orders, setOrders] = useState([])
    const [orderData, setOrderData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [materialsOption, setMaterialsOption] = useState('ALREADY_BOUGHT');
    const [requirements, setRequirements] = useState({
        contract: false,
        license: false,
        warranty: false,
        legalEntity: false,
    });

    // Состояния для дат
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [asSoonAsPossible, setAsSoonAsPossible] = useState(false);
    const [startDateError, setStartDateError] = useState('');
    const [endDateError, setEndDateError] = useState('');

    // Каталоги категорий и услуг
    const [catalog, setCatalog] = useState([]);
    const [loadingCatalog, setLoadingCatalog] = useState(true);
    const [catalogError, setCatalogError] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [selectedService, setSelectedService] = useState(null);
    const [customServiceName, setCustomServiceName] = useState('');

    const tabs = [
        { id: 'details', label: 'Детали заказа', icon: <img src={tab_detail} alt="" /> },
        { id: 'chat',    label: 'Чат со специалистами', icon: <img src={tab_chat} alt="" /> },
        {
            id: 'specialists',
            label: 'Все специалисты',
            icon: <img src={tab_specialists} alt="" />,
            onClick: () => {
            const mainService = orderData?.services?.[0];

            if (!mainService) {
                navigate('/customer_all_specialists');
                return;
            }

            navigate('/customer_all_specialists', {
                state: {
                preselectSubcategory: {
                    id: mainService.categoryId || mainService.subcategoryId,
                    name: mainService.categoryTitle || mainService.subcategoryTitle || mainService.serviceTitle,
                }
                }
            });
            }
        },
    ];
    
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768) 
    // определение ширины устройства
    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);

        return () => window.removeEventListener('resize', handleResize);
    }, []);  

    useEffect(() => {
        if (!isMobile || activeTab !== 'chat') return undefined;

        const previousBodyOverflow = document.body.style.overflow;
        const previousHtmlOverflow = document.documentElement.style.overflow;

        document.body.style.overflow = 'hidden';
        document.documentElement.style.overflow = 'hidden';

        return () => {
            document.body.style.overflow = previousBodyOverflow;
            document.documentElement.style.overflow = previousHtmlOverflow;
        };
    }, [activeTab, isMobile]);

    // Модалки редактирования
    const [showEditCategoryModal, setShowEditCategoryModal] = useState(false);
    const [showEditServiceModal, setShowEditServiceModal] = useState(false);
    const [showEditLocationModal, setShowEditLocationModal] = useState(false);
    const [showEditDeadlineModal, setShowEditDeadlineModal] = useState(false);
    const [showEditBudgetModal, setShowEditBudgetModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    // Состояния для редактируемых полей
    const [editCategory, setEditCategory] = useState('');
    const [editService, setEditService] = useState('');
    const [editLocation, setEditLocation] = useState('');
    const [editBudget, setEditBudget] = useState('');
    // Состояния для сохранения
    const [isSaving, setIsSaving] = useState(false);
    const [isLoading, setIsLoading] = useState(false)
    const deletingAttachmentIdsRef = useRef(new Set());


    // Вычисляемые значения услуг
    const topCategories = useMemo(() => {
        if (!catalog || !Array.isArray(catalog)) return [];

        const secondLevel = [];

        catalog.forEach(root => {
            if (Array.isArray(root.children)) {
                root.children.forEach(child => {
                    if (child.name?.trim()) {
                        secondLevel.push({
                            id: child.id,
                            name: child.name.trim(),
                        });
                    }
                });
            }
        });

        // сортировка по orderIndex, если важно
        secondLevel.sort((a, b) => {
            const aOrder = catalog.find(c => c.children?.some(ch => ch.id === a.id))?.children
                .find(ch => ch.id === a.id)?.orderIndex ?? 999;
            const bOrder = catalog.find(c => c.children?.some(ch => ch.id === b.id))?.children
                .find(ch => ch.id === b.id)?.orderIndex ?? 999;
            return aOrder - bOrder;
        });

        return secondLevel;
    }, [catalog]);

    const filteredServices = useMemo(() => {
        if (!selectedCategory || !catalog) return [];

        let targetSubCategory = null;

        // Ищем подкатегорию по её id среди всех children всех корневых категорий
        for (const root of catalog) {
            if (!Array.isArray(root.children)) continue;

            const found = root.children.find(child => child.id === selectedCategory.id);
            if (found) {
                targetSubCategory = found;
                break;
            }
        }

        if (!targetSubCategory) {
            console.warn("Не найдена подкатегория с id:", selectedCategory.id);
            return [];
        }

        const allServices = [];

        const collectServices = (node) => {
            if (Array.isArray(node.services)) {
                node.services.forEach(s => {
                    if (s?.name?.trim()) {
                        allServices.push({ id: s.id, name: s.name.trim() });
                    }
                });
            }
            if (Array.isArray(node.children)) {
                node.children.forEach(collectServices);
            }
        };

        collectServices(targetSubCategory);

        return allServices;
    }, [selectedCategory, catalog]);

    // Загрузка каталога услуг
    useEffect(() => {
        let isActive = true;
        const fetchCatalog = async () => {
            try {
                setLoadingCatalog(true);
                setCatalogError(null);
                const data = await getCachedServiceCatalog();
                if (isActive) setCatalog(data);
            } catch (err) {
                console.error('Ошибка каталога:', err);
                if (isActive) setCatalogError('Не удалось загрузить категории и услуги');
            } finally {
                if (isActive) setLoadingCatalog(false);
            }
        };
        fetchCatalog();
        return () => {
            isActive = false;
        };
    }, []);

    // Загрузка городов
    useEffect(() => {
        let isActive = true;
        const loadCities = async () => {
            try {
                setLoadingCities(true);
                const citiesData = await getCachedCities();

                // Дедупликация по ID (на всякий случай)
                const uniqueMap = new Map();
                citiesData.forEach(city => {
                    if (!uniqueMap.has(city.id)) {
                        uniqueMap.set(city.id, { id: city.id, name: city.name });
                    }
                });

                const allUniqueCities = Array.from(uniqueMap.values());

                // Топ-10 городов в правильном порядке
                const top10Names = ["Москва","Санкт-Петербург","Новосибирск", "Екатеринбург", "Казань", "Нижний Новгород", "Челябинск", "Красноярск", "Самара", "Уфа" ];

                // Выделяем топ-10
                const top10 = [];
                const top10Set = new Set(top10Names.map(n => n.toLowerCase()));

                allUniqueCities.forEach(city => {
                    if (top10Set.has(city.name.toLowerCase())) {
                        top10.push(city);
                    }
                });

                // Сортируем топ-10 по заданному порядку
                top10.sort((a, b) => {
                    const iA = top10Names.findIndex(n => n.toLowerCase() === a.name.toLowerCase());
                    const iB = top10Names.findIndex(n => n.toLowerCase() === b.name.toLowerCase());
                    return iA - iB;
                });

                // Остальные города по алфавиту
                const rest = allUniqueCities
                    .filter(c => !top10Set.has(c.name.toLowerCase()))
                    .sort((a, b) => a.name.localeCompare(b.name, 'ru'));

                const sortedCities = [...top10, ...rest];
                
                if (isActive) setCities(sortedCities);
                
            } catch (err) {
                console.error('Ошибка загрузки городов:', err);
            } finally {
                if (isActive) setLoadingCities(false);
            }
        };
        loadCities();
        return () => {
            isActive = false;
        };
    }, []);

    useEffect(() => {
        let isActive = true;
        const fetchOrderDetails = async () => {
        if (!orderId) {
            setError('ID заказа не указан');
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            const data = await getCachedCustomerOrder(orderId);
            if (!isActive) return;
            
            setOrderData(data);
            
            // Устанавливаем материалы
            if (data.materialsProvider) {
                setMaterialsOption(data.materialsProvider);
            }
            
            // Устанавливаем требования
            setRequirements({
                contract: data.requireContract || false,
                license: data.requireLicense || false,
                warranty: data.hasWarranty || false,
                legalEntity: data.onlyLegalEntity || false,
            });

        } catch (err) {
            console.error('Ошибка загрузки заказа:', err);
            if (isActive) setError('Не удалось загрузить данные заказа');
        } finally {
            if (isActive) setLoading(false);
        }
        };

        fetchOrderDetails();
        return () => {
            isActive = false;
        };
    }, [orderId]);

    // Открытие таба чата
    useEffect(() => {
        if (routerLocation.state?.activeTab) {
            setActiveTab(routerLocation.state.activeTab);
        }
    }, [routerLocation.state]);

    if (loading) {
        return (
        <div style={{ textAlign: 'center', padding: '120px 20px', fontSize: '20px' }}>
            Загрузка данных заказа...
        </div>
        );
    }

    if (error || !orderData) {
        return (
        <div style={{ textAlign: 'center', padding: '120px 20px', color: '#777', fontSize: '20px' }}>
            {error || 'Заказ не найден'}
            <br />
            <button
            onClick={() => navigate(-1)}
            style={{
                marginTop: '24px',
                padding: '12px 28px',
                backgroundColor: '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '16px',
                cursor: 'pointer',
            }}
            >
            Вернуться назад
            </button>
        </div>
        );
    }

    const {title, description, services = [], location, durationText, budgetRub, materialsProvider, requireContract, requireLicense, hasWarranty, onlyLegalEntity, attachments = [],} = orderData;

    const categoryName = services[0]?.categoryTitle || 'Категория не указана';
    const serviceName = services[0]?.serviceTitle || 'Услуга не указана';

    const getLocationText = () => {
        if (location?.cityName) {
        let text = location.cityName;
        if (location.districtName) text += `, ${location.districtName}`;
        if (location.metroStationName) text += ` (м. ${location.metroStationName})`;
        return text;
        }
        return 'Местоположение не указано';
    };



    // ===== ФУНКЦИИ ДЛЯ ОБРАБОТКИ ДАТ =====
    const parseDate = (dateStr) => {
        if (!dateStr || dateStr.length !== 10) return null;
        const parts = dateStr.split('.');
        if (parts.length !== 3) return null;
        
        const day = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1;
        const year = parseInt(parts[2], 10);
        
        if (isNaN(day) || isNaN(month) || isNaN(year)) return null;
        
        const date = new Date(year, month, day);
        
        // Проверяем валидность даты
        if (date.getDate() !== day || date.getMonth() !== month || date.getFullYear() !== year) {
            return null;
        }
        
        return date;
    };

    const isFutureDate = (dateStr) => {
        const parsed = parseDate(dateStr);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return parsed && !isNaN(parsed.getTime()) && parsed >= today;
    };

    const isValidDate = (dateStr) => {
        const parsed = parseDate(dateStr);
        return parsed !== null;
    };

    const isDateOrderValid = (startStr, endStr) => {
        const start = parseDate(startStr);
        const end = parseDate(endStr);
        if (!start || !end) return true;
        return start <= end;
    };

    const handleDateChange = (value, type) => {
        const digits = value.replace(/\D/g, '').slice(0, 8);
        let formatted = digits;
        
        // Форматируем с точками: ДД.ММ.ГГГГ
        if (digits.length > 4) {
            formatted = digits.slice(0, 2) + '.' + digits.slice(2, 4) + '.' + digits.slice(4, 8);
        } else if (digits.length > 2) {
            formatted = digits.slice(0, 2) + '.' + digits.slice(2);
        }

        if (type === 'start') {
            setStartDate(formatted);
            if (digits.length === 8) {
                if (!isValidDate(formatted)) {
                    setStartDateError('Некорректная дата');
                } else if (!isFutureDate(formatted)) {
                    setStartDateError('Дата не может быть в прошлом');
                } else {
                    setStartDateError('');
                }
            } else {
                setStartDateError('');
            }
        } else {
            setEndDate(formatted);
            if (digits.length === 8) {
                if (!isValidDate(formatted)) {
                    setEndDateError('Некорректная дата');
                } else if (!isFutureDate(formatted)) {
                    setEndDateError('Дата не может быть в прошлом');
                } else {
                    setEndDateError('');
                }
            } else {
                setEndDateError('');
            }
        }
    };


    // ===== ФУНКЦИИ СОХРАНЕНИЯ ИЗМЕНЕННЫХ ДАННЫХ ЗАКАЗА =====
    // Сохранение категории
    const handleSaveCategory = async () => {
        try {            
            // Обновляем отображаемые данные
            setOrderData(prev => ({
                ...prev,
                services: [{
                    ...prev.services[0],
                    categoryTitle: editCategory,
                    // Удаляем информацию об услуге
                    serviceTitle: null,
                    serviceId: null
                }]
            }));
            
            // Также сбрасываем выбранную услугу в состоянии
            setSelectedService(null);
            setEditService('');
            
            setShowEditCategoryModal(false);
        } catch (err) {
            console.error('Ошибка:', err);
            alert('Не удалось сохранить категорию');
        }
    };

    // Сохранение услуги
    const handleSaveService = async () => {
        try {            
            // Обновляем отображаемые данные
            setOrderData(prev => ({
                ...prev,
                services: [{
                    ...prev.services[0],
                    serviceTitle: editService,
                    serviceId: selectedService?.id || prev.services[0]?.serviceId
                }]
            }));
            
            setShowEditServiceModal(false);
        } catch (err) {
            console.error('Ошибка:', err);
            alert('Не удалось сохранить услугу');
        }
    };

    // Сохранение местоположения
    const handleSaveLocation = async () => {
        try {            
            // Находим выбранный город по названию
            const selectedCity = cities.find(c => c.name === editLocation);
            
            // Обновляем отображаемые данные
            setOrderData(prev => ({
                ...prev,
                location: {
                    ...prev.location,
                    cityName: editLocation,
                    cityId: selectedCity?.id || prev.location?.cityId || "",
                    regionId: selectedCity?.id || prev.location?.regionId || ""
                }
            }));
            
            setShowEditLocationModal(false);
        } catch (err) {
            console.error('Ошибка:', err);
            alert('Не удалось сохранить местоположение');
        }
    };

    // Сохранение сроков
    const handleSaveDeadline = async () => {
        try {
            let newDurationText = '';
            let newDurationValue = null;
            let newDurationUnit = null;
            
            if (asSoonAsPossible) {
                newDurationText = 'Как можно скорее';
            } else if (startDate && endDate) {
                newDurationText = `${startDate} – ${endDate}`;
                const start = parseDate(startDate);
                const end = parseDate(endDate);
                if (start && end) {
                    newDurationValue = Math.max(1, Math.ceil((end - start) / (1000 * 60 * 60 * 24)));
                    newDurationUnit = "DAYS";
                }
            }
            
            // Обновляем отображаемые данные
            setOrderData(prev => ({
                ...prev,
                durationText: newDurationText,
                durationValue: newDurationValue,
                durationUnit: newDurationUnit
            }));
            
            setShowEditDeadlineModal(false);
        } catch (err) {
            console.error('Ошибка:', err);
            alert('Не удалось сохранить сроки');
        }
    };

    // Сохранение бюджета
    const handleSaveBudget = async () => {
        try {            
            // Обновляем отображаемые данные
            setOrderData(prev => ({
                ...prev,
                budgetRub: editBudget ? Number(editBudget) : null
            }));
            
            setShowEditBudgetModal(false);
        } catch (err) {
            console.error('Ошибка:', err);
            alert('Не удалось сохранить бюджет');
        }
    };

    // Удаление вложения
    const handleDeleteAttachment = async (documentId) => {
        if (deletingAttachmentIdsRef.current.has(documentId)) return;
        if (!window.confirm('Удалить это вложение?')) return;

        deletingAttachmentIdsRef.current.add(documentId);
        try {
            await apiClient.delete(`/customers/me/orders/${orderId}/attachments/${documentId}`);
            
            // Обновляем локальное состояние
            setOrderData(prev => ({
                ...prev,
                attachments: prev.attachments.filter(att => att.documentId !== documentId)
            }));
            updateFrontendCache(`customer-order-${orderId}`, prev => ({
                ...prev,
                attachments: (prev.attachments || []).filter(att => att.documentId !== documentId)
            }));
            invalidatePersonalAccountCache('customer-orders');
            
        } catch (err) {
            console.error('Ошибка удаления вложения:', err);
            alert('Не удалось удалить файл');
        } finally {
            deletingAttachmentIdsRef.current.delete(documentId);
        }
    };

    // Загрузка новых вложений
    const handleUploadAttachments = async (files) => {
        if (isLoading || !files || files.length === 0) return;
        
        const MAX_FILES = 3;
        const MAX_SIZE_MB = 10;
        
        // Проверка количества файлов
        if (orderData.attachments.length + files.length > MAX_FILES) {
            alert(`Максимум ${MAX_FILES} файлов`);
            return;
        }
        
        // Проверка размера файлов
        for (const file of files) {
            if (file.size > MAX_SIZE_MB * 1024 * 1024) {
                alert(`Файл «${file.name}» слишком большой (макс. ${MAX_SIZE_MB} МБ)`);
                return;
            }
        }
        
        setIsLoading(true);
        
        try {
            const fd = new FormData();
            files.forEach(file => {
                fd.append("attachments", file);
            });
            
            const response = await apiClient.post(
                `/customers/me/orders/${orderId}/attachments`,
                fd,
                { headers: { "Content-Type": "multipart/form-data" } }
            );
            
            // Обновляем локальное состояние
            setOrderData(prev => ({
                ...prev,
                attachments: [...prev.attachments, ...(response.data.attachments || [])]
            }));
            updateFrontendCache(`customer-order-${orderId}`, prev => ({
                ...prev,
                attachments: [...(prev.attachments || []), ...(response.data.attachments || [])]
            }));
            invalidatePersonalAccountCache('customer-orders');
            
        } catch (err) {
            console.error('Ошибка загрузки файлов:', err);
            alert('Не удалось загрузить файлы');
        } finally {
            setIsLoading(false);
        }
    };


    // Главная функция сохранения всех изменений
    const handleSaveAllChanges = async () => {
        if (isSaving) return;
        setIsSaving(true);
        
        try {
            // Получаем актуальные значения из формы
            const descriptionValue = document.querySelector('textarea.description-input')?.value || description;
            
            // ИСПРАВЛЕНИЕ: Находим поле бюджета по более конкретному селектору
            // Ищем поле ввода внутри модалки редактирования бюджета или на главной странице
            let budgetValue = null;
            
            // Сначала проверяем, есть ли открытая модалка бюджета
            if (showEditBudgetModal) {
                const budgetInput = document.querySelector('.edit-modal-content input[type="number"]')?.value;
                budgetValue = budgetInput ? Number(budgetInput) : null;
            } else {
                // Если модалка не открыта, ищем поле на главной странице
                // Можно добавить id или класс к полю бюджета для точного определения
                const budgetInput = document.querySelector('.budget-input')?.value || editBudget;
                budgetValue = budgetInput ? Number(budgetInput) : null;
            }
            
            // Получаем ID выбранной услуги
            let serviceId = orderData.services[0]?.serviceId;
            
            // Если выбрана новая услуга через модалку
            if (selectedService?.id) {
                serviceId = selectedService.id;
            }
            
            // Получаем выбранный город из модалки редактирования
            const selectedCity = cities.find(c => c.name === editLocation);
            
            // Формируем payload в соответствии с API
            const payload = {
                serviceIds: [serviceId],
                description: descriptionValue,
                budgetRub: budgetValue, // Может быть null (договорная)
                requireContract: requirements.contract,
                requireLicense: requirements.license,
                onlyLegalEntity: requirements.legalEntity,
                hasWarranty: requirements.warranty,
                materialsProvider: materialsOption,
                location: {
                    regionType: "CITY",
                    cityId: selectedCity?.id || location?.cityId || "",
                    regionId: selectedCity?.regionId || location?.regionId || location?.cityId || ""
                }
            };
            
            // Добавляем title только если услуга "Другое" и есть кастомное название
            if (selectedService?.name === 'Другое' && customServiceName) {
                payload.title = customServiceName.trim();
            } else if (orderData.title) {
                // Если это не "Другое", но title был, передаём его
                payload.title = orderData.title;
            }
            
            // Добавляем сроки
            if (asSoonAsPossible) {
                payload.durationValue = null;
                payload.durationUnit = null;
            } else if (startDate && endDate) {
                const start = parseDate(startDate);
                const end = parseDate(endDate);
                if (start && end) {
                    payload.durationValue = Math.max(1, Math.ceil((end - start) / (1000 * 60 * 60 * 24)));
                    payload.durationUnit = "DAYS";
                }
            } else if (orderData.durationValue && orderData.durationUnit) {
                // Если даты не меняли, оставляем как было
                payload.durationValue = orderData.durationValue;
                payload.durationUnit = orderData.durationUnit;
            }
            
            console.log('Сохраняем заказ с payload:', JSON.stringify(payload, null, 2));
            
            // Отправляем PATCH запрос
            const response = await apiClient.patch(`/customers/me/orders/${orderId}`, payload);
            
            // Обновляем данные заказа из ответа
            const updatedOrder = response.data.order || response.data;
            setOrderData(updatedOrder);
            updateFrontendCache(`customer-order-${orderId}`, updatedOrder);
            invalidateFrontendCache(`order-detail-CUSTOMER-${orderId}`);
            invalidatePersonalAccountCache('customer-orders');
            
            // Обновляем editBudget из ответа
            if (updatedOrder.budgetRub) {
                setEditBudget(updatedOrder.budgetRub.toString());
            }
            
            alert('Изменения успешно сохранены');
            navigate(-1)
            
        } catch (err) {
            console.error('Ошибка сохранения:', err);
            let msg = 'Не удалось сохранить изменения';
            
            if (err.response) {
                const { status, data } = err.response;
                console.log('Детали ошибки:', data);
                
                if (status === 400) {
                    msg = data?.message || 'Некорректные данные';
                    if (data?.errors) {
                        const errorMessages = Object.values(data.errors).flat().join(', ');
                        msg += `: ${errorMessages}`;
                    }
                } else if (status === 401) msg = 'Требуется авторизация';
                else if (status === 403) msg = 'Доступ запрещён';
                else if (status === 404) msg = 'Заказ не найден';
                else if (status === 409) msg = 'Заказ нельзя редактировать (не в статусе черновика)';
            }
            
            alert(msg);
        } finally {
            setIsSaving(false);
        }
    };


    // Удаление заказа
    const handleDeleteOrder = async () => {
        if (isDeleting) return;
        setIsDeleting(true);
        
        try {
            await apiClient.delete(`/customers/me/orders/${orderId}`);
            invalidateFrontendCache(
                `customer-order-${orderId}`,
                `order-detail-CUSTOMER-${orderId}`
            );
            invalidatePersonalAccountCache('customer-orders');
            
            // Закрываем модалку
            setShowDeleteModal(false);
            
            // Показываем сообщение об успехе
            alert('Заказ успешно удалён');
            
            // Переходим на страницу с заказами
            navigate('/customer_my_orders');
            
        } catch (err) {
            console.error('Ошибка удаления заказа:', err);
            
            let errorMessage = 'Не удалось удалить заказ';
            
            if (err.response) {
                const { status } = err.response;
                
                if (status === 401) {
                    errorMessage = 'Требуется авторизация';
                } else if (status === 403) {
                    errorMessage = 'Доступ запрещён';
                } else if (status === 404) {
                    errorMessage = 'Заказ не найден';
                }
            }
            
            alert(errorMessage);
            setShowDeleteModal(false);
        } finally {
            setIsDeleting(false);
        }
    };

    
  return (
    <div className={`page-layout order-edit-page ${activeTab === 'chat' ? 'order-edit-chat-active' : ''}`}>
      <title>Детали заказа | Rulla</title>

        <Header rightContent={
            < >
                <button className='btn_user' onClick={()=>navigate('/customer_personal_account')}><img src={icon_user} alt='Иконка пользователя'/></button>
                {!isMobile && (
                    <button className='btn-blue' onClick={() => setShowCreateForm(true)}>Создать заказ</button>
                )}
            </> }
            menuItems={   [
                { label: 'О платформе', to: '/about' },
                { label: 'Каталог исполнителей', to: '/customer_all_specialists' },
                { label: 'Мои заказы', to: '/customer_my_orders' },
            ]}
            mobileMenuItems={[
                { label: 'О платформе', to: '/about' },
                { label: 'Каталог исполнителей', to: '/customer_all_specialists' },
                { label: 'Мои заказы', to: '/customer_my_orders' },
            ]}
            
            showMobileButton={true}
            onMobileButtonClick={() => setShowCreateForm(true)}
        />

      <div className="full-container" style={{ marginBottom: '220px',}}>
        <div className='main-container'>

            {showCreateForm ? (
                <CreateOrderForm
                    onClose={() => setShowCreateForm(false)}
                    onCreate={(newOrder) => {
                    setOrders(prev => [newOrder, ...prev])
                    setShowCreateForm(false)
                    }}
                    existingOrders={orders}
                />
            ) : (
                <>
                    <div className="page-header" style={{ marginBottom:isMobile?'0':'30px', display: 'flex', flexDirection: 'row'}}>
                        <img src={back} alt='Назад' onClick={()=>navigate(-1)} style={{margin:isMobile?'50px 10px 0 0':'110px 20px 0 0', cursor: 'pointer'}}/> 
                         <h1 className="page-title" style={{maxWidth: isMobile?'100%':'', textAlign:isMobile?'left':''}}>
                           {title}
                         </h1>
                    </div>

                    {/* Табы */}
                    {isMobile ? (
                        <div className="mobile-tab-selector">
                            <RegistrSelector
                                subject={tabs.map(tab => tab.label)}          
                                placeholder="Выберите раздел..."
                                selected={tabs.find(t => t.id === activeTab)?.label || ''}
                                onSelect={(selectedLabel) => {
                                    const foundTab = tabs.find(t => t.label === selectedLabel);
                                    if (foundTab) {
                                        setActiveTab(foundTab.id);
                                    }
                                }}
                                disabled={false}
                            />
                        </div>
                    ) : (
                        <div className="profile-tabs-container">
                            <div className="profile-tabs">
                            {tabs.map((tab) => (
                                <button
                                    key={tab.id}
                                    className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
                                    onClick={() => {
                                        if (tab.onClick) {
                                            tab.onClick();
                                        } else {
                                            setActiveTab(tab.id);
                                        }
                                    }}
                                >
                                    {tab.icon && <span className="tab-icon">{tab.icon}</span>}
                                    <span className="tab-label">{tab.label}</span>
                                </button>
                            ))}
                            </div>
                        </div>
                    )}

                    {/* Содержимое вкладок */}
                    <div className="profileContainer order" style={{padding:0, border:'none'}}>
                        {/* Вкладка "Детали заказа" */}
                        {activeTab === 'details' && (
                            <div className="order-edit-details-card" style={{padding: isMobile?'15px':'45px', border: '1px solid #919191', borderRadius:'10px'}}>
                                <div className="order-details-grid">
                                {/* Левая колонка */}
                                <div>
                                    <h2 className='detail-zagolovok'>Детали заказа</h2>
                                    
                                    {/* Категория */}
                                    <div className="detail-row">
                                        <div className="detail-label">Категория</div>
                                        <div className="detail-value-row">
                                            <span className="detail-value">{categoryName}</span>
                                            <button className="editButton"   
                                                onClick={() => {
                                                    setEditCategory(categoryName);
                                                    setShowEditCategoryModal(true);
                                                }}>
                                                    <img src={edit} alt="Редактировать" />
                                            </button>
                                        </div>
                                    </div>

                                    {/* Что нужно сделать */}
                                    <div className="detail-row">
                                        <div className="detail-label" style={{marginBottom:isMobile?'7px':''}}>Что нужно сделать</div>
                                        <div className="detail-value-row">
                                            <span className="detail-value">{serviceName}</span>
                                            <button className="editButton" onClick={() => {
                                                setEditService(serviceName);
                                                setShowEditServiceModal(true);
                                            }}>
                                                <img src={edit} alt="Редактировать" />
                                            </button>
                                        </div>
                                    </div>

                                    {/* Местоположение */}
                                    <div className="detail-row">
                                        <div className="detail-label">Местоположение</div>
                                        <div className="detail-value-row">
                                            <span className="detail-value">{getLocationText()}</span>
                                            <button className="editButton"   onClick={() => {
                                                setEditLocation(getLocationText());
                                                setShowEditLocationModal(true);
                                            }}>
                                                <img src={edit} alt="Редактировать" />
                                            </button>
                                        </div>
                                    </div>

                                    {/* Сроки выполнения */}
                                    <div className="detail-row">
                                        <div className="detail-label">Сроки выполнения</div>
                                        <div className="detail-value-row">
                                            <span className="detail-value">{durationText || 'Не указаны'}</span>
                                            <button className="editButton"   onClick={() => {
                                                setShowEditDeadlineModal(true);
                                            }}>
                                                <img src={edit} alt="Редактировать" />
                                            </button>
                                        </div>
                                    </div>

                                    {/* Требования к исполнителю */}
                                    <div className="detail-row">
                                        <div className="detail-label">Требования к исполнителю</div>
                                        
                                        <div className="requirements-grid">
                                            {[
                                                { label: 'Работа по договору', checked: requirements.contract, key: 'contract' },
                                                { label: 'Наличие лицензии', checked: requirements.license, key: 'license' },
                                                { label: 'Гарантия на работу', checked: requirements.warranty, key: 'warranty' },
                                                { label: 'Юридическое лицо', checked: requirements.legalEntity, key: 'legalEntity' },
                                            ].map((item) => (
                                                <div 
                                                    key={item.key} 
                                                    className="checkbox-wrapper" 
                                                    onClick={() => setRequirements(prev => ({
                                                        ...prev,
                                                        [item.key]: !prev[item.key]
                                                    }))}
                                                    style={{ cursor: 'pointer' }}
                                                >
                                                    <div className={`custom-checkbox ${item.checked ? 'checked' : ''}`}>
                                                        {item.checked && (
                                                            <svg width="14" height="10" viewBox="0 0 14 10" fill="none" className="check-icon">
                                                                <path d="M1 5L5 9L13 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                            </svg>
                                                        )}
                                                    </div>
                                                    <span className="checkbox-textt" style={{ fontSize: isMobile?'16px':'20px' }}>
                                                        {item.label}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Бюджет проекта */}
                                    <div className="detail-row">
                                        <div className="detail-label" style={{marginTop: '30px'}}>Бюджет проекта</div>
                                        <div className="detail-value-row">
                                            <span className="detail-value">
                                                {budgetRub ? `${budgetRub.toLocaleString('ru-RU')} ₽` : 'Договорная'}
                                            </span>
                                            <button className="editButton"   onClick={() => {
                                                setEditBudget(budgetRub?.toString() || '');
                                                setShowEditBudgetModal(true);
                                            }}>
                                                <img src={edit} alt="Редактировать" />
                                            </button>
                                        </div>
                                    </div>

                                    {/* Материалы */}
                                    <div>
                                        <div className="detail-label">Материалы</div>
                                        <div className="materials-options">
                                            {[
                                                { label: 'Уже куплены', value: 'ALREADY_BOUGHT' },
                                                { label: 'Закуплю сам', value: 'BUY_MYSELF' },
                                                { label: 'От исполнителя', value: 'FROM_EXECUTOR' }
                                            ].map((option) => (
                                                <label key={option.value} className="radio-option">
                                                    <input
                                                        type="radio"
                                                        name="materials"
                                                        value={option.value}
                                                        checked={materialsOption === option.value}
                                                        onChange={(e) => setMaterialsOption(e.target.value)}
                                                    />
                                                    {option.label}
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                {/* Правая колонка */}
                                <div>
                                    {/* Желаемый результат + загрузка файлов */}
                                    <div>
                                        <div className="detail-label" style={{marginTop: '10px'}}>Желаемый результат</div>
                                        <p style={{ fontSize: isMobile?'16px':'18px', color: '#000000B2', margin: '5px 0 10px 0', lineHeight: '1.2' }}>
                                            Добавьте 2-3 фото желаемого результата для наглядности (при необходимости)
                                        </p>

                                        <div className="file-upload-area">
                                            <FileUpload
                                                onFilesUpload={(files) => handleUploadAttachments(files)}
                                                maxFiles={3}
                                                accept="image/*,application/pdf,video/*"
                                            />
                                        </div>

                                        {/* Вложения отображаем ПОСЛЕ FileUpload */}
                                        {attachments.length > 0 && (
                                            <div style={{ marginTop: '20px' }}>
                                                <p style={{ fontSize: '16px', fontWeight: '500', marginBottom: '8px', color: '#000000B2' }}>
                                                    Загруженные файлы ({attachments.length}):
                                                </p>
                                                <div style={{ 
                                                    display: 'flex', 
                                                    flexWrap: 'wrap', 
                                                    gap: '10px',
                                                    marginTop: '10px'
                                                }}>
                                                    {attachments.map((att, idx) => {
                                                        const isImage = att.contentType?.startsWith('image/');
                                                        const fileName = att.url?.split('/').pop() || `Файл ${idx + 1}`;
                                                        
                                                        return (
                                                            <div key={att.documentId || idx} style={{ 
                                                                position: 'relative',
                                                                width: isMobile?'48%':'175px',
                                                                height:isMobile?'90px':'120px',
                                                                borderRadius: '8px',
                                                                overflow: 'hidden',
                                                                border: '1px solid #e0e0e0',
                                                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                                            }}>
                                                                {isImage ? (
                                                                    <img 
                                                                        src={att.url} 
                                                                        alt={fileName}
                                                                        style={{
                                                                            width: '100%',
                                                                            height: '100%',
                                                                            objectFit: 'cover',
                                                                            cursor: 'pointer'
                                                                        }}
                                                                        onClick={() => window.open(att.url, '_blank')}
                                                                        onError={(e) => {
                                                                            e.target.onerror = null;
                                                                            e.target.src = 'https://via.placeholder.com/120x120?text=Ошибка+загрузки';
                                                                        }}
                                                                    />
                                                                ) : (
                                                                    <div 
                                                                        style={{
                                                                            width: '100%',
                                                                            height: '100%',
                                                                            display: 'flex',
                                                                            flexDirection: 'column',
                                                                            alignItems: 'center',
                                                                            justifyContent: 'center',
                                                                            backgroundColor: '#f5f5f5',
                                                                            cursor: 'pointer',
                                                                            padding: '10px',
                                                                            textAlign: 'center'
                                                                        }}
                                                                        onClick={() => window.open(att.url, '_blank')}
                                                                    >
                                                                        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#666" strokeWidth="2">
                                                                            <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
                                                                            <polyline points="13 2 13 9 20 9" />
                                                                        </svg>
                                                                        <span style={{ 
                                                                            fontSize: '10px', 
                                                                            marginTop: '5px',
                                                                            wordBreak: 'break-word',
                                                                            maxWidth: '100px'
                                                                        }}>
                                                                            {fileName.substring(0, 10)}...
                                                                        </span>
                                                                    </div>
                                                                )}
                                                                
                                                                {/* Кнопка удаления */}
                                                                <button
                                                                    onClick={() => handleDeleteAttachment(att.documentId)}
                                                                    style={{
                                                                        position: 'absolute',
                                                                        top: '5px',
                                                                        right: '5px',
                                                                        width: '24px',
                                                                        height: '24px',
                                                                        borderRadius: '50%',
                                                                        background: 'rgba(255, 68, 68, 0.9)',
                                                                        border: 'none',
                                                                        cursor: 'pointer',
                                                                        color: 'white',
                                                                        fontSize: '16px',
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                        padding: 0,
                                                                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                                                                    }}
                                                                    title="Удалить"
                                                                >
                                                                    ×
                                                                </button>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Описание */}
                                    <div>
                                        <div className="detail-label" style={{marginTop: '25px'}}>Описание</div>
                                        <textarea
                                            placeholder="Добавьте описание своего проекта"
                                            className="description-input"
                                            rows={6}
                                            defaultValue={description || ''}
                                            style={{width: '100%', minHeight: '165px', marginTop: '5px'}}
                                        />
                                    </div>

                                    {/* Кнопка удалить */}
                                    <div style={{textAlign: 'left'}}>
                                        <button onClick={() => setShowDeleteModal(true)} className='delete-btn'><img src={btn_delete} alt=''style={{marginRight: '10px'}}/>
                                            Удалить заказ
                                        </button>
                                    </div>

                                    {/* Кнопка Сохранить */}
                                    <div style={{maxWidth: '100%', marginTop: '-20px'}}>
                                        <button className="save-btn" onClick={handleSaveAllChanges} disabled={isSaving} style={{height: '80px'}}>
                                            {isSaving ? 'Сохранение...' : 'Сохранить'}
                                        </button>
                                    </div>
                                </div>
                                </div>
                            </div>
                        )}

                        {/* Вкладка "Чат со специалистами" */}
                        {activeTab === 'chat' && (
                            <Chat 
                                orderId={orderId} 
                                openSupportOnLoad={routerLocation.state?.openSupport}/>
                        )}

                        {/* Вкладка "Все специалисты" */}
                        {activeTab === 'specialists' && (navigate('/customer_all_specialists'))}
                    </div>


                    {/* МОДАЛЬНЫЕ ОКНА РЕДАКТИРОВАНИЯ */}
                    {/* Модалка редактирования категории */}
                    {showEditCategoryModal && (
                        <div className="modal-overlay" onClick={() => setShowEditCategoryModal(false)}>
                            <div className="edit-modal-content" onClick={e => e.stopPropagation()}>
                                <div className="modal-header">
                                    <h2 style={{fontSize:isMobile?'18px':'', marginTop: isMobile?'30px':''}}>Редактировать категорию</h2>
                                    <button className="modal-close-btn" onClick={() => setShowEditCategoryModal(false)}>
                                        <img src={close} alt="Закрыть" />
                                    </button>
                                </div>
                                <form onSubmit={(e) => { e.preventDefault(); handleSaveCategory(); }}>
                                    <div className="passport-field full-width" style={{ margin: '40px 0 30px 0' }}>
                                        <h3 style={{fontSize:isMobile?'16px':'', marginTop: isMobile?'-20px':''}}>Категория</h3>
                                        <div className="registr-selector">
                                            <RegistrSelector
                                                placeholder="Выберите категорию"
                                                subject={topCategories.map(c => c.name)}
                                                onSelect={name => {
                                                    const cat = topCategories.find(c => c.name === name);
                                                    setSelectedCategory(cat);
                                                    setSelectedService(null);
                                                    setEditCategory(name);
                                                }}
                                                selected={editCategory}
                                            />
                                        </div>
                                    </div>
                                    <div className="modal-buttons">
                                        <button type="button" className="btn-cancel_" onClick={() => setShowEditCategoryModal(false)}>Отмена</button>
                                        <button type="submit" className="btn-save_">Сохранить</button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    )}

                    {/* Модалка редактирования услуги */}
                    {showEditServiceModal && (
                        <div className="modal-overlay" onClick={() => setShowEditServiceModal(false)}>
                            <div className="edit-modal-content" onClick={e => e.stopPropagation()}>
                                <div className="modal-header">
                                    <h2 style={{fontSize:isMobile?'18px':'', marginTop: isMobile?'30px':''}}>Редактировать услугу</h2>
                                    <button className="modal-close-btn" onClick={() => setShowEditServiceModal(false)}>
                                        <img src={close} alt="Закрыть" />
                                    </button>
                                </div>
                                <form onSubmit={(e) => { e.preventDefault(); handleSaveService(); }}>
                                    <div className="passport-field full-width" style={{ margin: '40px 0 30px 0' }}>
                                        <h3 style={{fontSize:isMobile?'16px':'', marginTop: isMobile?'-20px':''}}>Что нужно сделать?</h3>
                                        <div className="registr-selector">
                                            <RegistrSelector
                                                placeholder="Выберите услугу"
                                                subject={filteredServices.map(s => s.name)}
                                                onSelect={name => {
                                                    const serv = filteredServices.find(s => s.name === name);
                                                    setSelectedService(serv);
                                                    setEditService(name);
                                                    setCustomServiceName('');
                                                }}
                                                selected={editService}
                                                disabled={!selectedCategory || loadingCatalog}
                                            />
                                        </div>
                                        {selectedService?.name === 'Другое' && (
                                            <div style={{ marginTop: '12px' }}>
                                                <input
                                                    type="text"
                                                    className="description-input"
                                                    placeholder="Введите собственную услугу"
                                                    value={customServiceName}
                                                    onChange={e => setCustomServiceName(e.target.value)}
                                                />
                                            </div>
                                        )}
                                    </div>
                                    <div className="modal-buttons">
                                        <button type="button" className="btn-cancel_" onClick={() => setShowEditServiceModal(false)}>Отмена</button>
                                        <button type="submit" className="btn-save_">Сохранить</button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    )}

                    {/* Модалка редактирования местоположения */}
                    {showEditLocationModal && (
                        <div className="modal-overlay" onClick={() => setShowEditLocationModal(false)}>
                            <div className="edit-modal-content" onClick={e => e.stopPropagation()}>
                                <div className="modal-header">
                                    <h2 style={{fontSize:isMobile?'18px':'', marginTop: isMobile?'30px':''}}>Редактировать местоположение</h2>
                                    <button className="modal-close-btn" onClick={() => setShowEditLocationModal(false)}>
                                        <img src={close} alt="Закрыть" />
                                    </button>
                                </div>
                                <form onSubmit={(e) => { e.preventDefault(); handleSaveLocation(); }}>
                                    <div className="passport-field full-width" style={{ margin: '40px 0 30px 0' }}>
                                        <h3 style={{fontSize:isMobile?'16px':'', marginTop: isMobile?'-20px':''}}>Город</h3>
                                        <div className="registr-selector">
                                            <RegistrSelector
                                                placeholder="Выберите город"
                                                subject={cities.map(city => city.name)}
                                                onSelect={(name) => {
                                                    setEditLocation(name);
                                                }}
                                                selected={editLocation}
                                                disabled={loadingCities}
                                            />
                                        </div>
                                    </div>
                                    <div className="modal-buttons">
                                        <button type="button" className="btn-cancel_" onClick={() => setShowEditLocationModal(false)}>Отмена</button>
                                        <button type="submit" className="btn-save_">Сохранить</button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    )}

                    {/* Модалка редактирования сроков */}
                    {showEditDeadlineModal && (
                        <div className="modal-overlay" onClick={() => setShowEditDeadlineModal(false)}>
                            <div className="edit-modal-content" onClick={e => e.stopPropagation()}>
                                <div className="modal-header">
                                    <h2 style={{fontSize:isMobile?'18px':'', marginTop: isMobile?'30px':''}}>Редактировать сроки</h2>
                                    <button className="modal-close-btn" onClick={() => setShowEditDeadlineModal(false)}>
                                        <img src={close} alt="Закрыть" />
                                    </button>
                                </div>
                                <form onSubmit={(e) => { e.preventDefault(); handleSaveDeadline(); }}>
                                    <div className="passport-field full-width" style={{ margin: '40px 0 30px 0' }}>
                                        <h3 style={{fontSize:isMobile?'16px':'', marginTop: isMobile?'-20px':''}}>
                                            Сроки выполнения
                                            {(!asSoonAsPossible && (startDateError || endDateError || !isDateOrderValid(startDate, endDate))) && (
                                                <span style={{ color: '#ff4444', marginLeft: '10px', fontSize: '16px' }}>
                                                    {startDateError || endDateError || 'Дата окончания раньше начала'}
                                                </span>
                                            )}
                                        </h3>
                                        
                                        <div className="dates-row" style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
                                            <DatePicker
                                                value={startDate}
                                                onChange={v => handleDateChange(v, 'start')}
                                                placeholder="Начало"
                                                error={!!startDateError && !asSoonAsPossible}
                                                disabled={asSoonAsPossible}
                                            />
                                            <DatePicker
                                                value={endDate}
                                                onChange={v => handleDateChange(v, 'end')}
                                                placeholder="Конец"
                                                error={!!endDateError && !asSoonAsPossible}
                                                disabled={asSoonAsPossible}
                                            />
                                        </div>

                                        {/* Чекбокс "Как можно скорее" */}
                                        <div className="checkbox-wrapper" onClick={() => setAsSoonAsPossible(!asSoonAsPossible)} style={{ marginTop: '10px', marginBottom: isMobile?'-30px':'' }}>
                                            <div className={`custom-checkbox ${asSoonAsPossible ? 'checked' : ''}`}>
                                                {asSoonAsPossible && (
                                                    <svg width="14" height="10" viewBox="0 0 14 10" fill="none" className="check-icon">
                                                        <path d="M1 5L5 9L13 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                    </svg>
                                                )}
                                            </div>
                                            <span style={{ fontSize:isMobile?'16px':'20px', fontWeight: '500', color: '#000'}}>
                                                Как можно скорее
                                            </span>
                                        </div>
                                    </div>
                                    <div className="modal-buttons">
                                        <button type="button" className="btn-cancel_" onClick={() => setShowEditDeadlineModal(false)}>Отмена</button>
                                        <button type="submit" className="btn-save_">Сохранить</button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    )}

                    {/* Модалка редактирования бюджета */}
                    {showEditBudgetModal && (
                    <div className="modal-overlay" onClick={() => setShowEditBudgetModal(false)}>
                        <div className="edit-modal-content" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2 style={{fontSize:isMobile?'18px':'', marginTop: isMobile?'30px':''}}>Редактировать бюджет</h2>
                            <button className="modal-close-btn" onClick={() => setShowEditBudgetModal(false)}>
                            <img src={close} alt="Закрыть" />
                            </button>
                        </div>
                        <form onSubmit={(e) => { e.preventDefault(); handleSaveBudget(); }}>
                            <div className="passport-field full-width" style={{ margin: '40px 0 30px 0', position: 'relative' }}>
                                <h3 style={{fontSize:isMobile?'16px':'', marginTop: isMobile?'-20px':''}}>Бюджет проекта</h3>
                                <input
                                    type="number"
                                    value={editBudget}
                                    onChange={(e) => setEditBudget(e.target.value)}
                                    className="budget-input"
                                    placeholder="До"
                                    style={{ width: '100%', padding: '15px', fontSize: '16px', border: '1px solid #DE5A2B', borderRadius: '7px' }}
                                />
                                <span style={{ position: 'absolute', right: '15px', top:isMobile?'55%':'65%', transform: 'translateY(-50%)', color: '#656565', fontSize: '20px', pointerEvents: 'none' }}>₽</span>
                            </div>
                            <div className="modal-buttons">
                                <button type="button" className="btn-cancel_" onClick={() => setShowEditBudgetModal(false)}>Отмена</button>
                                <button type="submit" className="btn-save_">Сохранить</button>
                            </div>
                        </form>
                        </div>
                    </div>
                    )}

                    {/* Модалка подтверждения удаления */}
                    {showDeleteModal && (
                        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
                            <div className="modal-window" onClick={e => e.stopPropagation()}>
                                <button className="modal-close-btn" onClick={() => setShowDeleteModal(false)}>
                                    <img src={close} alt="Закрыть" />
                                </button>
                                
                                <div className="modal-text">
                                    Вы уверены, что хотите удалить заказ?
                                </div>
                                
                                <div className="btns-group">
                                    <button 
                                        className="btn_blue" 
                                        onClick={() => setShowDeleteModal(false)}
                                        disabled={isDeleting}
                                    >
                                        Отмена
                                    </button>
                                    <button 
                                        className="btn_oran" 
                                        onClick={handleDeleteOrder}
                                        disabled={isDeleting}
                                    >
                                        {isDeleting ? 'Удаление...' : 'Удалить'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </>
                 
            )}
        </div>
      </div>

      <Footer />
    </div>
  );
}
